<script setup>
import { computed, ref, watch } from 'vue';
import { formatSignedPercent } from '../format';
import { labelFor, tilePercent } from '../heatmap';
import { t } from '../i18n';

// Treemap of shares (docs/heatmap/SPEC.md): one SVG, a tile per share. Geometry comes from the parent and
// stays put while the screen is open; colours and percents are live (decision 9).
const props = defineProps({
  // [{ key, x, y, w, h, ticker, name, change, color, hint }]
  tiles: {
    type: Array,
    required: true,
  },
  width: {
    type: Number,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  desktop: {
    type: Boolean,
    default: false,
  },
  // Tile of the open sheet: outlined so it is clear what the sheet is about.
  selectedKey: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['select']);

// The page draws this SVG into a PNG for sharing and copying (decision 30).
const svg = ref(null);
defineExpose({ svg });

// Tiles read as separate cards, as in T-Invest (decision 37): 3 px between them, 7 px corners.
const GAP = 3;
// The map's own corners (decision 31): the tiles in them take this radius, so the outline follows it.
const CORNER = 24;
const EDGE = 0.5;

// Rounded rectangle with its own radius per corner: top-left, top-right, bottom-right, bottom-left.
function roundedRect(x, y, w, h, [tl, tr, br, bl]) {
  return `M${x + tl},${y}H${x + w - tr}A${tr},${tr} 0 0 1 ${x + w},${y + tr}`
    + `V${y + h - br}A${br},${br} 0 0 1 ${x + w - br},${y + h}`
    + `H${x + bl}A${bl},${bl} 0 0 1 ${x},${y + h - bl}`
    + `V${y + tl}A${tl},${tl} 0 0 1 ${x + tl},${y}Z`;
}

const cells = computed(() => props.tiles.map((tile) => {
  const w = Math.max(tile.w - GAP, 0);
  const h = Math.max(tile.h - GAP, 0);
  const title = tile.ticker ?? '';
  const percent = tilePercent(tile.change);
  const label = labelFor(title, percent, w, h, props.desktop);
  const x = tile.x + GAP / 2;
  const y = tile.y + GAP / 2;
  const left = tile.x <= EDGE;
  const top = tile.y <= EDGE;
  const right = tile.x + tile.w >= props.width - EDGE;
  const bottom = tile.y + tile.h >= props.height - EDGE;
  const small = Math.min(w, h) < 16 ? 3 : 7;
  const limit = Math.min(w, h) / 2;
  const radii = [top && left, top && right, bottom && right, bottom && left]
    .map((corner) => Math.min(corner ? CORNER : small, limit));
  return {
    ...tile,
    x,
    y,
    w,
    h,
    d: roundedRect(x, y, w, h, radii),
    // The outline is drawn inside the tile (2 px wide, centred 1 px in), so nothing cuts it.
    outline: roundedRect(x + 1, y + 1, Math.max(w - 2, 0), Math.max(h - 2, 0), radii.map((r) => Math.max(r - 1, 0))),
    title,
    percent: label?.percent ? percent : '',
    label,
    aria: t('heatmap.tileAria', { ticker: tile.ticker ?? '', name: tile.name ?? '', change: spokenChange(tile.change) }),
  };
}));

function spokenChange(change) {
  if (change === null || change === undefined) {
    return t('heatmap.noChange');
  }
  const text = formatSignedPercent(Math.abs(change)).replace(/^[+−]/, '');
  if (Number(Math.abs(change).toFixed(1)) === 0) {
    return text;
  }
  return change > 0 ? t('heatmap.plus', { value: text }) : t('heatmap.minus', { value: text });
}

// One tab stop for the whole map (roving tabindex): arrows move through tiles in size order.
const focusIndex = ref(0);
const tileRefs = ref([]);

watch(() => props.tiles.length, (length) => {
  if (focusIndex.value >= length) {
    focusIndex.value = 0;
  }
});

function onKeydown(event, index) {
  const last = cells.value.length - 1;
  let next = null;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    next = Math.min(index + 1, last);
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    next = Math.max(index - 1, 0);
  } else if (event.key === 'Home') {
    next = 0;
  } else if (event.key === 'End') {
    next = last;
  } else if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    emit('select', cells.value[index].key);
    return;
  }
  if (next !== null) {
    event.preventDefault();
    focusIndex.value = next;
    tileRefs.value[next]?.focus();
  }
}

// Desktop: a hint next to the hovered tile.
const hovered = ref(null);
const hoveredCell = computed(() => cells.value.find((c) => c.key === hovered.value) ?? null);
const hintStyle = computed(() => {
  const cell = hoveredCell.value;
  if (!cell) {
    return {};
  }
  const left = Math.min(Math.max(cell.x + cell.w / 2, 90), props.width - 90);
  const below = cell.y + cell.h + 64 <= props.height;
  return below
    ? { left: `${left}px`, top: `${cell.y + cell.h + 6}px` }
    : { left: `${left}px`, top: `${Math.max(cell.y - 6, 0)}px`, transform: 'translate(-50%, -100%)' };
});
</script>

<template>
  <div
    class="relative"
    :style="{ height: `${height}px` }"
  >
    <svg
      ref="svg"
      :width="width"
      :height="height"
      :viewBox="`0 0 ${width} ${height}`"
      role="group"
      :aria-label="t('heatmap.mapAria', { count: tiles.length })"
      class="block select-none"
    >
      <g>
        <g
          v-for="(cell, index) in cells"
          :key="cell.key"
          :ref="(el) => { tileRefs[index] = el; }"
          role="button"
          :tabindex="index === focusIndex ? 0 : -1"
          :aria-label="cell.aria"
          class="group cursor-pointer focus:outline-none"
          @click="focusIndex = index; emit('select', cell.key)"
          @keydown="onKeydown($event, index)"
          @focus="focusIndex = index"
          @mouseenter="desktop && (hovered = cell.key)"
          @mouseleave="hovered === cell.key && (hovered = null)"
        >
          <path
            :d="cell.d"
            :fill="cell.color"
            class="transition-[fill] duration-300 motion-reduce:transition-none"
          />
          <path
            :d="cell.outline"
            fill="none"
            stroke="#fff"
            stroke-width="2"
            data-outline
            pointer-events="none"
            class="group-focus-visible:opacity-100"
            :class="cell.key === selectedKey || cell.key === hovered ? 'opacity-100' : 'opacity-0'"
          />
          <text
            v-if="cell.label"
            :x="cell.x + cell.w / 2"
            :y="cell.y + cell.h / 2 + (cell.percent ? -(cell.label.percent * 1.15 + 2) / 2 : 0)"
            text-anchor="middle"
            dominant-baseline="central"
            fill="#fff"
            font-weight="600"
            :font-size="cell.label.ticker"
            pointer-events="none"
          >{{ cell.title }}</text>
          <text
            v-if="cell.label && cell.percent"
            :x="cell.x + cell.w / 2"
            :y="cell.y + cell.h / 2 + (cell.label.ticker * 1.15 + 2) / 2"
            text-anchor="middle"
            dominant-baseline="central"
            fill="#fff"
            :font-size="cell.label.percent"
            class="tabular-nums"
            pointer-events="none"
          >{{ cell.percent }}</text>
        </g>
      </g>
    </svg>
    <div
      v-if="desktop && hoveredCell"
      class="pointer-events-none absolute z-10 w-max max-w-64 -translate-x-1/2 rounded-lg border border-slate-700 bg-slate-900/95 px-3 py-2 text-[0.8125rem] shadow-xl"
      :style="hintStyle"
      aria-hidden="true"
    >
      <p class="font-medium text-slate-100">
        {{ hoveredCell.name || hoveredCell.ticker }}
      </p>
      <p class="text-slate-400">
        {{ hoveredCell.hint }}
      </p>
    </div>
  </div>
</template>
